import React, { useState, useEffect } from 'react';
import { BookOpen, Brain, Target, Zap, Sparkles, MessageSquare, Trash2, Plus, CheckCircle2, ChevronRight, ChevronLeft } from 'lucide-react';
import { ChatSummary } from '../services/ospFirebaseService';
import { VERBAL_TOPICS, ANALYTICAL_TOPICS, getDailyChallengePrompt, QUICK_DRILL_PROMPT } from '../constants/ospActivities';
import { useAuth } from '../AuthContext';
import { getStudyState } from '../services/studyPlanStore';
import { getTodayMissionProgress, isTaskCompleted, areAllTasksCompleted, TaskCompletionStatus } from '../services/missionTaskCompletion';
import { StudyPlan } from '../types/onboarding';
import { CircularProgress } from './common/CircularProgress';

interface OspDashboardProps {
    ospChatList: ChatSummary[];
    onOpenTutor: () => void;
    onSelectChat: (chatId: string) => void;
    onDeleteChat: (chatId: string) => void;
    onAutoPrompt: (prompt: string) => void;
}

const OspDashboard: React.FC<OspDashboardProps> = ({
    ospChatList,
    onOpenTutor,
    onSelectChat,
    onDeleteChat,
    onAutoPrompt,
}) => {
    const { currentUser } = useAuth();
    const [plan, setPlan] = useState<StudyPlan | null>(null);
    const [taskProgress, setTaskProgress] = useState<TaskCompletionStatus[]>([]);
    const [activityView, setActivityView] = useState<'main' | 'verbal' | 'analytical'>('main');

    const isCompleted = areAllTasksCompleted(plan);

    // Load study plan
    useEffect(() => {
        const loadPlan = async () => {
            if (currentUser) {
                try {
                    const state = await getStudyState(currentUser.uid);
                    if (state?.studyPlan) {
                        setPlan(state.studyPlan);
                    }
                } catch (e) {
                    console.error('[OspDashboard] Failed to load study plan:', e);
                }
            }
        };
        loadPlan();
    }, [currentUser]);

    // Load task progress
    useEffect(() => {
        if (plan) {
            const progress = getTodayMissionProgress(plan);
            setTaskProgress(progress);
        }
    }, [plan]);

    // Listen for task completion events
    useEffect(() => {
        const handleTaskCompleted = () => {
            if (plan) {
                setTaskProgress(getTodayMissionProgress(plan));
            }
        };
        window.addEventListener('taskCompleted', handleTaskCompleted);
        return () => window.removeEventListener('taskCompleted', handleTaskCompleted);
    }, [plan]);

    // Filter OSP-only tasks
    const ospTasks = plan?.todayMission.tasks.filter(t =>
        t.module === 'OSP' || t.deepLink?.includes('/osp')
    ) || [];

    const ospTaskProgress = taskProgress.filter(tp =>
        ospTasks.some(t => t.id === tp.taskId)
    );

    const completedOspTasks = ospTaskProgress.filter(t => t.completed).length;

    // Relative time helper
    const getRelativeTime = (date: Date) => {
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return 'právě teď';
        if (diffMin < 60) return `před ${diffMin} min`;
        const diffHours = Math.floor(diffMin / 60);
        if (diffHours < 24) return `před ${diffHours} h`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays === 1) return 'včera';
        return `před ${diffDays} dny`;
    };

    return (
        <div className="overflow-y-auto p-4 sm:p-6 lg:p-8 h-full">
            <div className="max-w-3xl mx-auto space-y-6">

                {/* Section 1: Daily Missions */}
                {ospTasks.length > 0 && (
                    <div className="bg-white dark:bg-dark-card rounded-3xl p-6 border border-border shadow-[0_4px_12px_rgba(0,0,0,0.1)] dark:shadow-none">
                        {/* Header */}
                        <div className="mb-5">
                            <h2 className="text-xl font-bold font-poppins flex items-center gap-2">
                                {isCompleted ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                ) : (
                                    <CheckCircle2 className="w-5 h-5 text-brand-orange" />
                                )}
                                {isCompleted ? "Dnešní mise splněna!" : "Dnešní OSP mise"}
                            </h2>
                            <p className="text-sm text-muted-foreground ml-7">
                                {isCompleted
                                    ? "Skvělá práce! Pokračuj zítra."
                                    : `${completedOspTasks}/${ospTasks.length} úkolů hotovo`}
                            </p>
                        </div>

                        {/* Progress + Tasks */}
                        <div className={`grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 items-center ${isCompleted ? 'opacity-50 grayscale' : ''}`}>
                            <div className="flex justify-center">
                                <CircularProgress
                                    percentage={ospTasks.length > 0 ? (completedOspTasks / ospTasks.length) * 100 : 0}
                                    completedTasks={completedOspTasks}
                                    totalTasks={ospTasks.length}
                                    size={120}
                                    strokeWidth={12}
                                    animate={true}
                                />
                            </div>
                            <div className="space-y-2">
                                {ospTasks.slice(0, 4).map((task, index) => {
                                    const done = isTaskCompleted(task.id);
                                    return (
                                        <div
                                            key={task.id}
                                            className="flex items-center justify-between py-2 px-3 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-colors cursor-pointer group"
                                            onClick={() => {
                                                if (task.deepLink) {
                                                    onAutoPrompt(`Procvič se mnou: ${task.label}`);
                                                }
                                            }}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${done
                                                    ? 'bg-green-100 text-green-600 dark:bg-green-900/30'
                                                    : 'bg-secondary dark:bg-white/10 text-muted-foreground group-hover:bg-brand-orange/10 group-hover:text-brand-orange'
                                                    }`}>
                                                    {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : index + 1}
                                                </span>
                                                <span className={`text-sm font-semibold ${done ? 'line-through text-muted-foreground' : 'group-hover:text-brand-orange'}`}>
                                                    {task.label}
                                                </span>
                                            </div>
                                            <span className="text-xs text-muted-foreground">{task.minutes} min</span>
                                        </div>
                                    );
                                })}
                                {ospTasks.length > 4 && (
                                    <p className="text-xs text-center text-muted-foreground pt-1">+ {ospTasks.length - 4} další</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Section 2: Activity Shortcuts */}
                <div className="bg-white dark:bg-dark-card rounded-3xl p-6 border border-border shadow-[0_4px_12px_rgba(0,0,0,0.1)] dark:shadow-none">
                    <h2 className="text-lg font-bold font-poppins mb-4">Co chceš procvičovat?</h2>

                    {activityView === 'main' && (
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => setActivityView('verbal')}
                                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-neutral-50 dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 hover:border-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 active:border-b-2 active:translate-y-[2px] transition-all group"
                            >
                                <BookOpen className="w-7 h-7 text-neutral-500 group-hover:text-brand-orange transition-colors" />
                                <span className="text-sm font-bold text-neutral-600 dark:text-neutral-300 group-hover:text-brand-orange transition-colors">Verbální oddíl</span>
                            </button>
                            <button
                                onClick={() => setActivityView('analytical')}
                                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-neutral-50 dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 hover:border-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 active:border-b-2 active:translate-y-[2px] transition-all group"
                            >
                                <Brain className="w-7 h-7 text-neutral-500 group-hover:text-brand-orange transition-colors" />
                                <span className="text-sm font-bold text-neutral-600 dark:text-neutral-300 group-hover:text-brand-orange transition-colors">Analytický oddíl</span>
                            </button>
                            <button
                                onClick={() => onAutoPrompt(getDailyChallengePrompt())}
                                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-neutral-50 dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 hover:border-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 active:border-b-2 active:translate-y-[2px] transition-all group"
                            >
                                <Target className="w-7 h-7 text-neutral-500 group-hover:text-brand-orange transition-colors" />
                                <span className="text-sm font-bold text-neutral-600 dark:text-neutral-300 group-hover:text-brand-orange transition-colors">Denní výzva</span>
                            </button>
                            <button
                                onClick={() => onAutoPrompt(QUICK_DRILL_PROMPT)}
                                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-neutral-50 dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 hover:border-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 active:border-b-2 active:translate-y-[2px] transition-all group"
                            >
                                <Zap className="w-7 h-7 text-neutral-500 group-hover:text-brand-orange transition-colors" />
                                <span className="text-sm font-bold text-neutral-600 dark:text-neutral-300 group-hover:text-brand-orange transition-colors">Rychlý drill</span>
                            </button>
                        </div>
                    )}

                    {activityView === 'verbal' && (
                        <div className="space-y-2 animate-in fade-in slide-in-from-right-4 duration-300">
                            <button
                                onClick={() => setActivityView('main')}
                                className="flex items-center gap-2 mb-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                <span className="font-semibold">Verbální oddíl</span>
                            </button>
                            {VERBAL_TOPICS.map(topic => (
                                <button
                                    key={topic.title}
                                    onClick={() => {
                                        onAutoPrompt(`Procvič se mnou ${topic.title} z verbálního oddílu.`);
                                        setActivityView('main');
                                    }}
                                    className="flex flex-col items-start gap-1 w-full p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800 border border-border hover:border-brand-orange hover:shadow-sm transition-all text-left group"
                                >
                                    <span className="text-sm font-semibold text-foreground group-hover:text-brand-orange transition-colors">{topic.title}</span>
                                    <span className="text-[11px] text-muted-foreground leading-tight">{topic.desc}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {activityView === 'analytical' && (
                        <div className="space-y-2 animate-in fade-in slide-in-from-right-4 duration-300">
                            <button
                                onClick={() => setActivityView('main')}
                                className="flex items-center gap-2 mb-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                <span className="font-semibold">Analytický oddíl</span>
                            </button>
                            {ANALYTICAL_TOPICS.map(topic => (
                                <button
                                    key={topic.title}
                                    onClick={() => {
                                        onAutoPrompt(`Procvič se mnou ${topic.title} z analytického oddílu.`);
                                        setActivityView('main');
                                    }}
                                    className="flex flex-col items-start gap-1 w-full p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800 border border-border hover:border-brand-orange hover:shadow-sm transition-all text-left group"
                                >
                                    <span className="text-sm font-semibold text-foreground group-hover:text-brand-orange transition-colors">{topic.title}</span>
                                    <span className="text-[11px] text-muted-foreground leading-tight">{topic.desc}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Section 3: Open Tutor CTA */}
                <button
                    onClick={onOpenTutor}
                    className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-bold font-poppins text-lg shadow-lg border-b-[6px] border-orange-600 active:border-b-2 active:translate-y-[4px] transition-all flex items-center justify-center gap-3 group hover:shadow-xl"
                >
                    <Sparkles className="w-6 h-6" />
                    Otevřít AI Tutora
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>

                {/* Section 4: Chat History */}
                {ospChatList.length > 0 && (
                    <div className="bg-white dark:bg-dark-card rounded-3xl p-6 border border-border shadow-[0_4px_12px_rgba(0,0,0,0.1)] dark:shadow-none">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold font-poppins">Poslední konverzace</h2>
                            <button
                                onClick={onOpenTutor}
                                className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Nový chat
                            </button>
                        </div>
                        <div className="space-y-1">
                            {ospChatList.slice(0, 8).map((chat) => (
                                <div
                                    key={chat.chatId}
                                    onClick={() => onSelectChat(chat.chatId)}
                                    className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors cursor-pointer group"
                                >
                                    <MessageSquare className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                                    <span className="text-sm flex-1 min-w-0 truncate">{chat.title}</span>
                                    <span className="text-[11px] text-muted-foreground flex-shrink-0">{getRelativeTime(chat.updatedAt)}</span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteChat(chat.chatId);
                                        }}
                                        className="flex-shrink-0 p-1 rounded text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OspDashboard;
